import {
  Box,
  Button,
  Card,
  CardContent,
  Paper,
  TextField,
  Typography,
  Divider,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  MenuItem,
} from '@mui/material';
import { Edit, SwapHoriz, Business, Security } from '@mui/icons-material';
import { getProfile, getToken, getCurrentOrgId } from 'client/modules/Auth/AuthReducer';
import callApi from 'client/util/apiCaller';
import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

const messages = defineMessages({
  title: {
    id: 'General.Title',
    defaultMessage: 'General Settings',
  },
  organizationName: {
    id: 'General.OrganizationName',
    defaultMessage: 'Organization name',
  },
  organizationOwner: {
    id: 'General.OrganizationOwner',
    defaultMessage: 'Organization owner',
  },
  transferOwnership: {
    id: 'General.TransferOwnership',
    defaultMessage: 'Transfer ownership',
  },
  edit: {
    id: 'General.Edit',
    defaultMessage: 'Edit',
  },
  save: {
    id: 'General.Save',
    defaultMessage: 'Save',
  },
  cancel: {
    id: 'General.Cancel',
    defaultMessage: 'Cancel',
  },
  privacyAndSecurity: {
    id: 'General.PrivacyAndSecurity',
    defaultMessage: 'Privacy and Security',
  },
  termsOfService: {
    id: 'General.TermsOfService',
    defaultMessage: 'Terms of Service',
  },
  privacyPolicy: {
    id: 'General.PrivacyPolicy',
    defaultMessage: 'Privacy Policy',
  },
  dataProcessingAgreement: {
    id: 'General.DataProcessingAgreement',
    defaultMessage: 'Data Processing Agreement',
  },
  cookiesPolicy: {
    id: 'General.CookiesPolicy',
    defaultMessage: 'Cookies Policy',
  },

  transferOwnershipDialog: {
    id: 'General.TransferOwnershipDialog',
    defaultMessage: 'Transfer ownership',
  },
  transferOwnershipDescription: {
    id: 'General.TransferOwnershipDescription',
    defaultMessage:
      'Transferring ownership will make another user the owner of this organization. You will become an admin member.',
  },
  selectNewOwner: {
    id: 'General.SelectNewOwner',
    defaultMessage: 'Select new owner',
  },
  confirmTransfer: {
    id: 'General.ConfirmTransfer',
    defaultMessage: 'Transfer ownership',
  },
  verifyEmail: {
    id: 'General.VerifyEmail',
    defaultMessage: 'Verify email',
  },
  verificationCode: {
    id: 'General.VerificationCode',
    defaultMessage: 'Verification code',
  },
  verificationCodeDescription: {
    id: 'General.VerificationCodeDescription',
    defaultMessage:
      'Enter the verification code we sent to your email address to confirm the ownership transfer.',
  },
  sendVerificationCode: {
    id: 'General.SendVerificationCode',
    defaultMessage: 'Send verification code',
  },
  verificationCodeSent: {
    id: 'General.VerificationCodeSent',
    defaultMessage: 'Verification code sent to your email address',
  },
  invalidVerificationCode: {
    id: 'General.InvalidVerificationCode',
    defaultMessage: 'Invalid verification code. Please try again.',
  },
  ownershipTransferred: {
    id: 'General.OwnershipTransferred',
    defaultMessage: 'Ownership has been transferred successfully',
  },
  teammateRequired: {
    id: 'General.TeammateRequired',
    defaultMessage:
      'You can only transfer ownership to an existing teammate. Please invite them to your organization first.',
  },
  errorLoadingOrganization: {
    id: 'General.ErrorLoadingOrganization',
    defaultMessage: 'Error loading organization information',
  },
  organizationNameUpdated: {
    id: 'General.OrganizationNameUpdated',
    defaultMessage: 'Organization name updated successfully',
  },
  errorUpdatingName: {
    id: 'General.ErrorUpdatingName',
    defaultMessage: 'Error updating organization name',
  },
});

const GeneralPage = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const token = useSelector(getToken);
  const profile = useSelector(getProfile);
  const currentOrgId = useSelector(getCurrentOrgId);

  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferStep, setTransferStep] = useState(0); // 0: select user, 1: verify email
  const [selectedNewOwner, setSelectedNewOwner] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);

  useEffect(() => {
    if (currentOrgId && token) {
      loadOrganization();
    }
  }, [currentOrgId, token]);

  const loadOrganization = async () => {
    try {
      setLoading(true);
      setError(null);
      const [orgResponse, membersResponse] = await Promise.all([
        callApi(dispatch, `organizations/${currentOrgId}`, token, 'GET'),
        callApi(dispatch, `organizations/${currentOrgId}/members`, token, 'GET'),
      ]);
      setOrganization(orgResponse.organization);
      setMembers(membersResponse.members || []);
      setNewName(orgResponse.organization.name);
    } catch (err) {
      setError(formatMessage(messages.errorLoadingOrganization));
      console.error('Error loading organization:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditName = () => {
    setEditingName(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancelEdit = () => {
    setEditingName(false);
    setNewName(organization?.name || '');
  };

  const handleSaveName = async () => {
    try {
      setSaving(true);
      setError(null);

      const response = await callApi(dispatch, `organizations/${currentOrgId}`, token, 'PUT', {
        name: newName.trim(),
      });

      setOrganization(response.organization);
      setEditingName(false);
      setSuccess(formatMessage(messages.organizationNameUpdated));
    } catch (err) {
      setError(formatMessage(messages.errorUpdatingName));
      console.error('Error updating organization name:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleTransferOwnership = () => {
    setTransferDialogOpen(true);
    setTransferStep(0);
    setSelectedNewOwner('');
    setVerificationCode('');
    setError(null);
  };

  const handleSendVerificationCode = async () => {
    if (!selectedNewOwner) {
      setError('Please select a new owner');
      return;
    }

    try {
      setTransferLoading(true);
      await callApi(
        dispatch,
        `organizations/${currentOrgId}/transfer-ownership/verify`,
        token,
        'POST',
        {
          newOwnerId: selectedNewOwner,
        },
      );
      setTransferStep(1);
      setSuccess(formatMessage(messages.verificationCodeSent));
    } catch (err) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleConfirmTransfer = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    try {
      setTransferLoading(true);
      await callApi(
        dispatch,
        `organizations/${currentOrgId}/transfer-ownership/confirm`,
        token,
        'POST',
        {
          newOwnerId: selectedNewOwner,
          verificationCode: verificationCode.trim(),
        },
      );

      setSuccess(formatMessage(messages.ownershipTransferred));
      setTransferDialogOpen(false);
      await loadOrganization(); // Reload to get updated organization data
    } catch (err) {
      setError(formatMessage(messages.invalidVerificationCode));
    } finally {
      setTransferLoading(false);
    }
  };

  const handleCloseTransferDialog = () => {
    setTransferDialogOpen(false);
    setTransferStep(0);
    setSelectedNewOwner('');
    setVerificationCode('');
    setError(null);
    setSuccess(null);
  };

  const getCurrentUserRole = () => {
    if (!organization || !profile?.user_id) return null;
    const member = organization.members?.find(
      m => m.user === profile.user_id || m.user?._id === profile.user_id,
    );
    return member?.role;
  };

  const getOwnerInfo = () => {
    if (!organization) return null;
    const owner = organization.members?.find(m => m.role === 'owner');
    return owner;
  };

  const isCurrentUserOwner = () => {
    return getCurrentUserRole() === 'owner';
  };

  if (loading) {
    return (
      <Paper style={{ padding: '2em' }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {formatMessage(messages.title)}
        </Typography>
        <Typography>Loading...</Typography>
      </Paper>
    );
  }

  if (error && !organization) {
    return (
      <Paper style={{ padding: '2em' }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {formatMessage(messages.title)}
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  const owner = getOwnerInfo();

  return (
    <Paper style={{ padding: '2em', marginBottom: '2em' }}>
      <Helmet title={formatMessage(messages.title)} />

      <Typography variant="h5" component="h2" gutterBottom>
        {formatMessage(messages.title)}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Organization Name */}
        <Card variant="outlined">
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business sx={{ color: 'text.secondary' }} />
                <Typography variant="h6" component="h3">
                  {formatMessage(messages.organizationName)}
                </Typography>
              </Box>
              {!editingName && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<Edit />}
                  onClick={handleEditName}
                  disabled={!isCurrentUserOwner()}
                >
                  {formatMessage(messages.edit)}
                </Button>
              )}
            </Box>

            {editingName ? (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  variant="outlined"
                  size="small"
                  disabled={saving}
                />
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveName}
                    disabled={saving || !newName.trim() || newName.trim() === organization?.name}
                  >
                    {formatMessage(messages.save)}
                  </Button>
                  <Button variant="text" size="small" onClick={handleCancelEdit} disabled={saving}>
                    {formatMessage(messages.cancel)}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography variant="body1" sx={{ mt: 1, fontSize: '1.1rem' }}>
                {organization?.name || 'Unnamed Organization'}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Organization Owner */}
        <Card variant="outlined">
          <CardContent>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                  {formatMessage(messages.organizationOwner)}
                </Typography>
                {owner ? (
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {owner.user?.email || owner.user || 'Unknown user'}
                  </Typography>
                ) : (
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    No owner assigned
                  </Typography>
                )}
              </Box>

              {isCurrentUserOwner() && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<SwapHoriz />}
                  onClick={handleTransferOwnership}
                >
                  {formatMessage(messages.transferOwnership)}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        <Divider sx={{ my: 2 }} />

        {/* Privacy and Security */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Security sx={{ color: 'text.secondary' }} />
            <Typography variant="h6" component="h3">
              {formatMessage(messages.privacyAndSecurity)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Link href="/legal/terms" target="_blank" underline="hover">
              {formatMessage(messages.termsOfService)}
            </Link>
            <Link href="/legal/privacy" target="_blank" underline="hover">
              {formatMessage(messages.privacyPolicy)}
            </Link>

            <Link href="/legal/dpa" target="_blank" underline="hover">
              {formatMessage(messages.dataProcessingAgreement)}
            </Link>
            <Link href="/legal/cookies" target="_blank" underline="hover">
              {formatMessage(messages.cookiesPolicy)}
            </Link>
          </Box>
        </Box>
      </Box>

      {/* Transfer Ownership Dialog */}
      <Dialog open={transferDialogOpen} onClose={handleCloseTransferDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{formatMessage(messages.transferOwnershipDialog)}</DialogTitle>
        <DialogContent>
          <Stepper activeStep={transferStep} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>{formatMessage(messages.selectNewOwner)}</StepLabel>
            </Step>
            <Step>
              <StepLabel>{formatMessage(messages.verifyEmail)}</StepLabel>
            </Step>
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && transferStep === 1 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {transferStep === 0 && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {formatMessage(messages.transferOwnershipDescription)}
              </Typography>

              {members.filter(m => m.role !== 'owner' && m.status === 'accepted').length === 0 ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {formatMessage(messages.teammateRequired)}
                </Alert>
              ) : (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>{formatMessage(messages.selectNewOwner)}</InputLabel>
                  <Select
                    value={selectedNewOwner}
                    onChange={e => setSelectedNewOwner(e.target.value)}
                    label={formatMessage(messages.selectNewOwner)}
                  >
                    {members
                      .filter(m => m.role !== 'owner' && m.status === 'accepted' && m.user)
                      .map(member => (
                        <MenuItem
                          key={member.user._id || member.user}
                          value={member.user._id || member.user}
                        >
                          {member.user.email || member.user} ({member.role})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}

          {transferStep === 1 && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {formatMessage(messages.verificationCodeDescription)}
              </Typography>
              <TextField
                fullWidth
                label={formatMessage(messages.verificationCode)}
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                inputProps={{ maxLength: 6 }}
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTransferDialog} disabled={transferLoading}>
            {formatMessage(messages.cancel)}
          </Button>

          {transferStep === 0 && (
            <Button
              onClick={handleSendVerificationCode}
              variant="contained"
              disabled={!selectedNewOwner || transferLoading}
              startIcon={transferLoading ? <CircularProgress size={20} /> : null}
            >
              {formatMessage(messages.sendVerificationCode)}
            </Button>
          )}

          {transferStep === 1 && (
            <Button
              onClick={handleConfirmTransfer}
              variant="contained"
              disabled={!verificationCode.trim() || transferLoading}
              startIcon={transferLoading ? <CircularProgress size={20} /> : null}
            >
              {formatMessage(messages.confirmTransfer)}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default GeneralPage;
